import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  Collapse,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tab,
  Tabs,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import {
  CheckCircle,
  Error,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Schedule,
  TrendingUp,
  Warning,
  People,
} from "@mui/icons-material";

import { format, subDays } from "date-fns";

import useApi from "../../hooks/useApi";
import Page from "./Page";

const SYNC_DASHBOARD_CACHE_TTL_MS = 60000;
let lastSyncDashboardSnapshot = {
  filtersKey: "",
  dashboardData: null,
  recentActivity: null,
  at: 0,
};

const SyncDashboard = () => {
  const navigate = useNavigate();

  const { postData } = useApi();
  const latestRequestRef = useRef(0);
  const [loading, setLoading] = useState(() => {
    const hasWarmData =
      !!lastSyncDashboardSnapshot.dashboardData ||
      !!lastSyncDashboardSnapshot.recentActivity;
    return !hasWarmData;
  });
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(
    lastSyncDashboardSnapshot.dashboardData || null,
  );
  const [recentActivity, setRecentActivity] = useState(
    lastSyncDashboardSnapshot.recentActivity || null,
  );
  const [activeTab, setActiveTab] = useState(0);
  const [recentSyncPage, setRecentSyncPage] = useState(0);
  const [failureQueuePage, setFailureQueuePage] = useState(0);
  const [errorSignaturePage, setErrorSignaturePage] = useState(0);
  const [topUserPage, setTopUserPage] = useState(0);
  const [recentSyncAttemptsByRow, setRecentSyncAttemptsByRow] = useState({});
  const [recentSyncAttemptsLoadingByRow, setRecentSyncAttemptsLoadingByRow] =
    useState({});
  const [recentSyncAttemptsErrorByRow, setRecentSyncAttemptsErrorByRow] =
    useState({});
  const [expandedAttemptTelemetryByRow, setExpandedAttemptTelemetryByRow] =
    useState({});
  const [attemptTelemetryBySyncId, setAttemptTelemetryBySyncId] = useState({});
  const [attemptTelemetryLoadingBySyncId, setAttemptTelemetryLoadingBySyncId] =
    useState({});
  const [attemptTelemetryErrorBySyncId, setAttemptTelemetryErrorBySyncId] =
    useState({});
  const [expandedEntityBySyncId, setExpandedEntityBySyncId] = useState({});
  const [syncDetailModal, setSyncDetailModal] = useState({
    open: false,
    rowKey: "",
    sync: null,
  });
  const defaultDateRange = useMemo(() => {
    const now = new Date();
    return {
      dateFrom: format(now, "yyyy-MM-dd"),
      dateTo: format(now, "yyyy-MM-dd"),
    };
  }, []);

  const [filters, setFilters] = useState({
    province: "",
    supervisorId: "",
    repId: "",
    ...defaultDateRange,
  });

  const rowsPerPage = 10;

  const isValidDateInput = (value) => {
    const text = String(value || "").trim();
    if (!text) return true;
    return /^\d{4}-\d{2}-\d{2}$/.test(text);
  };

  const hasValidDateRange = () => {
    const from = String(filters?.dateFrom || "").trim();
    const to = String(filters?.dateTo || "").trim();

    if (!isValidDateInput(from) || !isValidDateInput(to)) {
      return false;
    }

    if (from && to && from > to) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!hasValidDateRange()) {
      setError("Please provide a valid date range");
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      loadDashboardData();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const handleToolBarClick = (item) => {
    switch (item) {
      case "callDashboard":
        navigate("/home");
        break;

      case "syncDashboard":
        navigate("/syncdashboard");
        break;

      case "surveyDashboard":
        navigate("/surveymetrics");
        break;

      case "orderDashboard":
        navigate("/orderdashboard");
        break;

      case "clientDashboard":
        navigate("/clientdashboard");
        break;

      case "repDashboard":
        navigate("/repdashboard");
        break;
    }
  };

  const loadDashboardData = async () => {
    const requestId = Date.now();
    latestRequestRef.current = requestId;

    try {
      const hasExistingData = !!dashboardData || !!recentActivity;
      if (!hasExistingData) {
        setLoading(true);
      }

      const [statsResult, activityResult] = await Promise.allSettled([
        postData({
          entityName: "SyncDashboard",
          action: "getDashboardStats",
          data: { filters },
        }),
        postData({
          entityName: "SyncDashboard",
          action: "getRecentActivity",
          data: { filters },
        }),
      ]);

      if (latestRequestRef.current !== requestId) {
        return;
      }

      const statsResponse =
        statsResult.status === "fulfilled" ? statsResult.value : null;
      const activityResponse =
        activityResult.status === "fulfilled" ? activityResult.value : null;

      const statsSuccess = Boolean(statsResponse?.isSuccessful);
      const activitySuccess = Boolean(activityResponse?.isSuccessful);

      if (statsSuccess) {
        setDashboardData(statsResponse.data || null);
      }
      if (activitySuccess) {
        setRecentActivity(activityResponse.data || null);
      }

      if (statsSuccess || activitySuccess) {
        setError(null);
        lastSyncDashboardSnapshot = {
          filtersKey: JSON.stringify(filters || {}),
          dashboardData: statsSuccess
            ? (statsResponse?.data ?? null)
            : dashboardData,
          recentActivity: activitySuccess
            ? (activityResponse?.data ?? null)
            : recentActivity,
          at: Date.now(),
        };
      } else {
        setError(
          statsResponse?.message ||
            activityResponse?.message ||
            "Failed to load dashboard data",
        );
      }
    } catch (err) {
      if (latestRequestRef.current !== requestId) {
        return;
      }
      setError(err.message);
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color = "primary",
  }) => (
    <Card elevation={2}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="h6"
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              component="div"
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {Icon && (
            <Icon sx={{ fontSize: 48, color: `${color}.main`, opacity: 0.7 }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "error":
        return "error";
      case "timeout":
        return "warning";
      case "processing":
      case "downloading":
      case "uploading":
        return "info";
      default:
        return "default";
    }
  };

  const formatDateTime = (dateValue, dateFormat = "MMM dd, HH:mm") => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";

    return format(date, dateFormat);
  };

  const formatDuration = (durationMs) => {
    const value = Number(durationMs);
    if (!Number.isFinite(value) || value <= 0) return "-";

    const totalSeconds = Math.floor(value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  };

  const formatObjectDetails = (value) => {
    if (!value || typeof value !== "object") return "-";

    try {
      return JSON.stringify(value, null, 2);
    } catch (_err) {
      return "-";
    }
  };

  const collectNumericValuesByKeys = (source, keys, depth = 0) => {
    if (!source || typeof source !== "object" || depth > 3) {
      return [];
    }

    const results = [];
    const entries = Object.entries(source);

    entries.forEach(([key, rawValue]) => {
      const normalizedKey = String(key || "").toLowerCase();
      const numericValue = Number(rawValue);

      if (keys.includes(normalizedKey) && Number.isFinite(numericValue)) {
        results.push(Math.max(0, numericValue));
      }

      if (rawValue && typeof rawValue === "object") {
        results.push(...collectNumericValuesByKeys(rawValue, keys, depth + 1));
      }
    });

    return results;
  };

  const deriveEntityCounts = (row) => {
    const successKeys = [
      "successcount",
      "success",
      "succeeded",
      "uploaded",
      "downloaded",
      "processedsuccess",
      "recordssuccess",
    ];
    const errorKeys = [
      "errorcount",
      "errors",
      "failed",
      "failurecount",
      "recordserror",
      "recordsfailed",
    ];
    const totalKeys = [
      "totalrecords",
      "total",
      "recordcount",
      "processed",
      "records",
      "expectedcount",
    ];
    const downloadedKeys = [
      "downloaded",
      "downloadcount",
      "downloadedcount",
      "recordsdownloaded",
      "fetched",
      "pulled",
      "downloadedonfirstsync",
    ];
    const uploadedKeys = [
      "uploaded",
      "uploadcount",
      "uploadedcount",
      "recordsuploaded",
      "pushed",
      "acknowledged",
    ];

    const detailsSources = [
      row?.lastDetails,
      ...(Array.isArray(row?.events)
        ? row.events.map((event) => event?.details)
        : []),
    ].filter((item) => item && typeof item === "object");

    const successCandidates = detailsSources.flatMap((source) =>
      collectNumericValuesByKeys(source, successKeys),
    );
    const errorCandidates = detailsSources.flatMap((source) =>
      collectNumericValuesByKeys(source, errorKeys),
    );
    const totalCandidates = detailsSources.flatMap((source) =>
      collectNumericValuesByKeys(source, totalKeys),
    );
    const downloadedCandidates = detailsSources.flatMap((source) =>
      collectNumericValuesByKeys(source, downloadedKeys),
    );
    const uploadedCandidates = detailsSources.flatMap((source) =>
      collectNumericValuesByKeys(source, uploadedKeys),
    );

    const success =
      successCandidates.length > 0 ? Math.max(...successCandidates) : null;
    const error =
      errorCandidates.length > 0 ? Math.max(...errorCandidates) : null;
    const fallbackTotal =
      (success || 0) + (error || 0) > 0 ? (success || 0) + (error || 0) : null;
    const total =
      totalCandidates.length > 0 ? Math.max(...totalCandidates) : fallbackTotal;
    const downloaded =
      downloadedCandidates.length > 0
        ? Math.max(...downloadedCandidates)
        : null;
    const uploaded =
      uploadedCandidates.length > 0 ? Math.max(...uploadedCandidates) : null;

    return {
      success,
      error,
      total,
      downloaded,
      uploaded,
      hasKnownCounts:
        Number.isFinite(success) ||
        Number.isFinite(error) ||
        Number.isFinite(total) ||
        Number.isFinite(downloaded) ||
        Number.isFinite(uploaded),
    };
  };

  const toNonNegativeCount = (value, fallback = 0) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(0, parsed);
  };

  const getSplitCallDetails = (details, subtype) => {
    if (!details || typeof details !== "object") {
      return details;
    }

    const prefix = subtype === "callResults" ? "callResults" : "calls";
    const downloaded = toNonNegativeCount(details?.[`${prefix}Downloaded`], 0);
    const uploaded = toNonNegativeCount(details?.[`${prefix}Uploaded`], 0);
    const processed = toNonNegativeCount(
      details?.[`${prefix}InsertedOrUpdated`],
      0,
    );

    return {
      ...details,
      downloaded,
      uploaded,
      processed,
      insertedOrUpdated: processed,
      success: processed,
      total: processed,
    };
  };

  const hasSplitCallSubtypeData = (row, subtype) => {
    const prefix = subtype === "callResults" ? "callResults" : "calls";
    const keys = [
      `${prefix}Downloaded`,
      `${prefix}Uploaded`,
      `${prefix}InsertedOrUpdated`,
    ];

    const detailsSources = [
      row?.lastDetails,
      ...(Array.isArray(row?.events)
        ? row.events.map((event) => event?.details)
        : []),
    ].filter((item) => item && typeof item === "object");

    return detailsSources.some((source) =>
      keys.some((key) => Number.isFinite(Number(source?.[key]))),
    );
  };

  const normalizeEntityRowsForDisplay = (rows) => {
    const entityRows = Array.isArray(rows) ? rows : [];

    return entityRows.flatMap((row) => {
      const normalizedEntity = String(row?.entity || "")
        .trim()
        .toLowerCase();

      if (normalizedEntity !== "calls") {
        return [row];
      }

      const hasCallsData = hasSplitCallSubtypeData(row, "calls");
      const hasCallResultsData = hasSplitCallSubtypeData(row, "callResults");

      if (!hasCallsData && !hasCallResultsData) {
        return [row];
      }

      const toSplitRow = (label, subtype) => ({
        ...row,
        entity: label,
        lastDetails: getSplitCallDetails(row?.lastDetails, subtype),
        events: Array.isArray(row?.events)
          ? row.events.map((event) => ({
              ...event,
              details: getSplitCallDetails(event?.details, subtype),
            }))
          : [],
      });

      const splitRows = [];
      if (hasCallsData) {
        splitRows.push(toSplitRow("calls", "calls"));
      }
      if (hasCallResultsData) {
        splitRows.push(toSplitRow("callResults", "callResults"));
      }

      return splitRows.length > 0 ? splitRows : [row];
    });
  };

  const summarizeEntityRows = (rows) => {
    const entityRows = Array.isArray(rows) ? rows : [];

    return entityRows.reduce(
      (summary, row) => {
        const normalizedStatus = String(row?.currentStatus || "")
          .trim()
          .toLowerCase();
        const eventCount = Number(row?.eventCount || 0);
        const counts = deriveEntityCounts(row);

        summary.total += 1;
        summary.totalEvents += Number.isFinite(eventCount)
          ? Math.max(0, eventCount)
          : 0;
        if (eventCount > 0) {
          summary.entitiesWithEvents += 1;
        }

        if (normalizedStatus === "completed") summary.completed += 1;
        else if (normalizedStatus === "error") summary.error += 1;
        else if (normalizedStatus === "timeout") summary.timeout += 1;
        else if (
          normalizedStatus === "processing" ||
          normalizedStatus === "downloading" ||
          normalizedStatus === "uploading"
        ) {
          summary.inProgress += 1;
        } else if (normalizedStatus) {
          summary.other += 1;
        }

        if (counts.hasKnownCounts) {
          summary.entitiesWithKnownCounts += 1;
        }
        summary.successRecords += Number.isFinite(counts.success)
          ? counts.success
          : 0;
        summary.errorRecords += Number.isFinite(counts.error)
          ? counts.error
          : 0;
        summary.totalRecords += Number.isFinite(counts.total)
          ? counts.total
          : 0;
        summary.downloadedRecords += Number.isFinite(counts.downloaded)
          ? counts.downloaded
          : 0;
        summary.uploadedRecords += Number.isFinite(counts.uploaded)
          ? counts.uploaded
          : 0;

        return summary;
      },
      {
        total: 0,
        completed: 0,
        error: 0,
        timeout: 0,
        inProgress: 0,
        other: 0,
        entitiesWithEvents: 0,
        totalEvents: 0,
        entitiesWithKnownCounts: 0,
        successRecords: 0,
        errorRecords: 0,
        totalRecords: 0,
        downloadedRecords: 0,
        uploadedRecords: 0,
      },
    );
  };

  const getAttemptSyncId = (attempt) =>
    String(attempt?.synchronizationId || attempt?._id || "");

  const loadAttemptEntityTelemetry = async (attemptSyncId) => {
    if (!attemptSyncId) return;

    setAttemptTelemetryLoadingBySyncId((previous) => ({
      ...previous,
      [attemptSyncId]: true,
    }));
    setAttemptTelemetryErrorBySyncId((previous) => ({
      ...previous,
      [attemptSyncId]: "",
    }));

    try {
      const response = await postData({
        entityName: "SyncDashboard",
        action: "getAttemptEntityTelemetry",
        data: {
          synchronizationId: attemptSyncId,
        },
      });

      if (response?.isSuccessful) {
        setAttemptTelemetryBySyncId((previous) => ({
          ...previous,
          [attemptSyncId]: response?.data || null,
        }));
      } else {
        setAttemptTelemetryErrorBySyncId((previous) => ({
          ...previous,
          [attemptSyncId]: response?.message || "Unable to load entity details",
        }));
      }
    } catch (err) {
      setAttemptTelemetryErrorBySyncId((previous) => ({
        ...previous,
        [attemptSyncId]: err?.message || "Unable to load entity details",
      }));
    } finally {
      setAttemptTelemetryLoadingBySyncId((previous) => ({
        ...previous,
        [attemptSyncId]: false,
      }));
    }
  };

  const toggleAttemptTelemetry = async (rowKey, attempt) => {
    const attemptSyncId = getAttemptSyncId(attempt);
    if (!attemptSyncId) return;

    const currentlyExpanded = expandedAttemptTelemetryByRow[rowKey];
    if (currentlyExpanded === attemptSyncId) {
      setExpandedAttemptTelemetryByRow((previous) => ({
        ...previous,
        [rowKey]: "",
      }));
      return;
    }

    setExpandedAttemptTelemetryByRow((previous) => ({
      ...previous,
      [rowKey]: attemptSyncId,
    }));

    if (
      !attemptTelemetryBySyncId[attemptSyncId] &&
      !attemptTelemetryLoadingBySyncId[attemptSyncId]
    ) {
      await loadAttemptEntityTelemetry(attemptSyncId);
    }
  };

  const toggleEntityDetail = (attemptSyncId, entity) => {
    setExpandedEntityBySyncId((previous) => ({
      ...previous,
      [attemptSyncId]:
        previous[attemptSyncId] === entity ? "" : String(entity || ""),
    }));
  };

  const openSyncDetailModal = async (sync) => {
    const rowKey = getRecentSyncRowKey(sync);
    setSyncDetailModal({
      open: true,
      rowKey,
      sync,
    });

    if (
      !recentSyncAttemptsByRow[rowKey] &&
      !recentSyncAttemptsLoadingByRow[rowKey]
    ) {
      await loadRecentSyncAttempts(sync, rowKey);
    }
  };

  const closeSyncDetailModal = () => {
    setSyncDetailModal({
      open: false,
      rowKey: "",
      sync: null,
    });
  };

  const refreshSyncDetailModal = async () => {
    if (!modalSync || !modalRowKey) return;

    await loadRecentSyncAttempts(modalSync, modalRowKey);

    const expandedAttemptSyncId = expandedAttemptTelemetryByRow[modalRowKey];
    if (expandedAttemptSyncId) {
      await loadAttemptEntityTelemetry(expandedAttemptSyncId);
    }
  };

  const getRecentSyncRowKey = (sync) =>
    `${String(sync.repId || "unknown")}-${String(sync.syncDay || "day")}`;

  const loadRecentSyncAttempts = async (sync, rowKey) => {
    if (!sync?.repId || !sync?.syncDay) {
      setRecentSyncAttemptsErrorByRow((previous) => ({
        ...previous,
        [rowKey]: "Missing rep/day selection",
      }));
      return;
    }

    setRecentSyncAttemptsLoadingByRow((previous) => ({
      ...previous,
      [rowKey]: true,
    }));
    setRecentSyncAttemptsErrorByRow((previous) => ({
      ...previous,
      [rowKey]: "",
    }));

    try {
      const attemptResponse = await postData({
        entityName: "SyncDashboard",
        action: "getRecentSyncAttempts",
        data: {
          filters,
          repId: String(sync.repId),
          syncDay: String(sync.syncDay),
        },
      });

      if (attemptResponse?.isSuccessful) {
        setRecentSyncAttemptsByRow((previous) => ({
          ...previous,
          [rowKey]: Array.isArray(attemptResponse?.data)
            ? attemptResponse.data
            : [],
        }));
      } else {
        setRecentSyncAttemptsErrorByRow((previous) => ({
          ...previous,
          [rowKey]: attemptResponse?.message || "Unable to load attempts",
        }));
      }
    } catch (err) {
      setRecentSyncAttemptsErrorByRow((previous) => ({
        ...previous,
        [rowKey]: err?.message || "Unable to load attempts",
      }));
    } finally {
      setRecentSyncAttemptsLoadingByRow((previous) => ({
        ...previous,
        [rowKey]: false,
      }));
    }
  };

  const formatDeviceType = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "-";

    const normalized = raw.toLowerCase();
    if (normalized.includes("android")) return "Android";
    if (normalized.includes("ios") || normalized.includes("iphone")) {
      return "iOS";
    }

    return raw;
  };

  const buildRepSyncMetaLine = (sync) => {
    const device = formatDeviceType(sync?.lastDeviceType);
    const osVersion = String(sync?.lastOsVersion || "").trim() || "-";
    const appVersion = String(sync?.lastAppVersion || "").trim() || "-";
    return `${device} | OS ${osVersion} | App ${appVersion}`;
  };

  const openAttemptsFromFailureQueue = async (failureRow) => {
    const sync = {
      repId: failureRow?.repId,
      syncDay: failureRow?.syncDay,
      repName: failureRow?.repName,
      lastSyncAt: failureRow?.lastSyncAt,
      lastSyncStatus: failureRow?.lastSyncStatus,
      successfulThatDay: false,
      attempts: failureRow?.attempts || 0,
      completedAttempts: failureRow?.completedAttempts || 0,
      errorAttempts: failureRow?.errorAttempts || 0,
      lastError: failureRow?.lastError || "",
    };

    const rows = Array.isArray(recentActivity?.recentSyncs)
      ? recentActivity.recentSyncs
      : [];
    const rowIndex = rows.findIndex(
      (item) =>
        String(item?.repId || "") === String(sync.repId || "") &&
        String(item?.syncDay || "") === String(sync.syncDay || ""),
    );

    setActiveTab(0);
    if (rowIndex >= 0) {
      setRecentSyncPage(Math.floor(rowIndex / rowsPerPage));
    }
    await openSyncDetailModal(sync);
  };

  const filterOptions = useMemo(() => {
    const provinceSet = new Set();
    const supervisorMap = new Map();
    const repMap = new Map();

    const addProvince = (value) => {
      const province = String(value || "").trim();
      if (province && province !== "-") {
        provinceSet.add(province);
      }
    };

    const addPerson = (map, id, name) => {
      const safeId = id ? String(id) : "";
      const safeName = String(name || "").trim();
      if (
        !safeId ||
        !safeName ||
        safeName === "Unknown" ||
        safeName === "Unassigned"
      )
        return;
      if (!map.has(safeId)) {
        map.set(safeId, safeName);
      }
    };

    (dashboardData?.users?.topUsers || []).forEach((item) => {
      addProvince(item.province);
      addPerson(supervisorMap, item.supervisorId, item.supervisorName);
      addPerson(repMap, item._id, item.repName || item.userName);
    });

    (recentActivity?.recentSyncs || []).forEach((item) => {
      addProvince(item.province);
      addPerson(supervisorMap, item.supervisorId, item.supervisorName);
      addPerson(
        repMap,
        item.repId || item.normalizedUserId || item.userId,
        item.repName,
      );
    });

    (recentActivity?.recentErrors || []).forEach((item) => {
      addProvince(item.province);
      addPerson(supervisorMap, item.supervisorId, item.supervisorName);
      addPerson(repMap, item.repId || item.userId, item.repName);
    });

    return {
      provinces: Array.from(provinceSet).sort((a, b) => a.localeCompare(b)),
      supervisors: Array.from(supervisorMap.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      reps: Array.from(repMap.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [
    dashboardData?.users?.topUsers,
    recentActivity?.recentSyncs,
    recentActivity?.recentErrors,
  ]);

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((previous) => ({ ...previous, [key]: value }));
    setRecentSyncPage(0);
    setFailureQueuePage(0);
    setErrorSignaturePage(0);
    setTopUserPage(0);
  };

  const resetPagination = () => {
    setRecentSyncPage(0);
    setFailureQueuePage(0);
    setErrorSignaturePage(0);
    setTopUserPage(0);
  };

  const applyDatePreset = (preset) => {
    const now = new Date();

    let dateFrom = filters.dateFrom;
    let dateTo = filters.dateTo;

    if (preset === "today") {
      dateFrom = format(now, "yyyy-MM-dd");
      dateTo = format(now, "yyyy-MM-dd");
    } else if (preset === "last7") {
      dateFrom = format(subDays(now, 6), "yyyy-MM-dd");
      dateTo = format(now, "yyyy-MM-dd");
    } else if (preset === "last30") {
      dateFrom = format(subDays(now, 29), "yyyy-MM-dd");
      dateTo = format(now, "yyyy-MM-dd");
    } else if (preset === "yesterdayToday") {
      dateFrom = format(subDays(now, 1), "yyyy-MM-dd");
      dateTo = format(now, "yyyy-MM-dd");
    }

    setFilters((previous) => ({
      ...previous,
      dateFrom,
      dateTo,
    }));

    resetPagination();
  };

  useEffect(() => {
    const cacheAge = Date.now() - Number(lastSyncDashboardSnapshot.at || 0);
    const currentFiltersKey = JSON.stringify(filters || {});
    const cacheKeyMatches =
      currentFiltersKey === lastSyncDashboardSnapshot.filtersKey;

    if (
      cacheKeyMatches &&
      cacheAge >= 0 &&
      cacheAge < SYNC_DASHBOARD_CACHE_TTL_MS &&
      (lastSyncDashboardSnapshot.dashboardData ||
        lastSyncDashboardSnapshot.recentActivity)
    ) {
      setDashboardData(lastSyncDashboardSnapshot.dashboardData || null);
      setRecentActivity(lastSyncDashboardSnapshot.recentActivity || null);
      setLoading(false);
    }
  }, [filters]);

  if (loading && !dashboardData) {
    return (
      <Page pageName="syncDashboard">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (error) {
    return (
      <Page pageName="syncDashboard">
        <Alert severity="error">{error}</Alert>
      </Page>
    );
  }

  const { stats, errors, users, performance } = dashboardData || {};
  const modalRowKey = syncDetailModal?.rowKey || "";
  const modalSync = syncDetailModal?.sync || null;
  const modalAttempts = modalRowKey
    ? recentSyncAttemptsByRow[modalRowKey] || []
    : [];
  const modalAttemptsLoading = modalRowKey
    ? Boolean(recentSyncAttemptsLoadingByRow[modalRowKey])
    : false;
  const modalAttemptsError = modalRowKey
    ? recentSyncAttemptsErrorByRow[modalRowKey] || ""
    : "";
  const modalAttemptSummary = modalAttempts.reduce(
    (summary, attempt) => {
      const status = String(attempt?.status || "")
        .trim()
        .toLowerCase();
      const successCount = Number(attempt?.successCount || 0);
      const errorCount = Number(attempt?.errorCount || 0);
      const totalRecords = Number(attempt?.totalRecords || 0);

      summary.total += 1;
      summary.successRecords += Number.isFinite(successCount)
        ? Math.max(0, successCount)
        : 0;
      summary.errorRecords += Number.isFinite(errorCount)
        ? Math.max(0, errorCount)
        : 0;
      summary.totalRecords += Number.isFinite(totalRecords)
        ? Math.max(0, totalRecords)
        : 0;

      if (status === "completed") summary.completedAttempts += 1;
      else if (status === "error") summary.errorAttempts += 1;
      else if (status === "timeout") summary.timeoutAttempts += 1;
      else if (
        status === "processing" ||
        status === "downloading" ||
        status === "uploading"
      ) {
        summary.inProgressAttempts += 1;
      }

      return summary;
    },
    {
      total: 0,
      completedAttempts: 0,
      errorAttempts: 0,
      timeoutAttempts: 0,
      inProgressAttempts: 0,
      successRecords: 0,
      errorRecords: 0,
      totalRecords: 0,
    },
  );
  const modalKnownRecords = Math.max(
    modalAttemptSummary.totalRecords,
    modalAttemptSummary.successRecords + modalAttemptSummary.errorRecords,
  );
  const modalRecordsLabel =
    modalKnownRecords > 0
      ? `Records: ${modalAttemptSummary.successRecords}/${modalKnownRecords} success`
      : "Records: unknown";

  return (
    <Page
      pageName="syncDashboard"
      includeToolbar={true}
      onToolItemClick={handleToolBarClick}
      items={[
        { name: "callDashboard", iconName: "CalendarMonth" },
        { name: "surveyDashboard", iconName: "Assignment" },
        { name: "orderDashboard", iconName: "ShoppingCart" },
        { name: "clientDashboard", iconName: "Business" },
        { name: "repDashboard", iconName: "People" },
      ]}
    >
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: 220 }}
                >
                  <InputLabel>Province</InputLabel>
                  <Select
                    label="Province"
                    value={filters.province}
                    onChange={handleFilterChange("province")}
                  >
                    <MenuItem value="">All Provinces</MenuItem>
                    {filterOptions.provinces.map((province) => (
                      <MenuItem
                        key={province}
                        value={province}
                      >
                        {province}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: 220 }}
                >
                  <InputLabel>Supervisor</InputLabel>
                  <Select
                    label="Supervisor"
                    value={filters.supervisorId}
                    onChange={handleFilterChange("supervisorId")}
                  >
                    <MenuItem value="">All Supervisors</MenuItem>
                    {filterOptions.supervisors.map((supervisor) => (
                      <MenuItem
                        key={supervisor.id}
                        value={supervisor.id}
                      >
                        {supervisor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: 220 }}
                >
                  <InputLabel>Rep</InputLabel>
                  <Select
                    label="Rep"
                    value={filters.repId}
                    onChange={handleFilterChange("repId")}
                  >
                    <MenuItem value="">All Reps</MenuItem>
                    {filterOptions.reps.map((rep) => (
                      <MenuItem
                        key={rep.id}
                        value={rep.id}
                      >
                        {rep.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={filters.dateFrom}
                  onChange={handleFilterChange("dateFrom")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={filters.dateTo}
                  onChange={handleFilterChange("dateTo")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyDatePreset("today")}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyDatePreset("last7")}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyDatePreset("last30")}
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyDatePreset("yesterdayToday")}
                  >
                    Yesterday - Today
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <Grid
          container
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <StatCard
              title="Total Syncs"
              value={stats?.totalSyncs || 0}
              subtitle={`${stats?.syncs24h || 0} in last 24h`}
              icon={TrendingUp}
              color="primary"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <StatCard
              title="Rep Sync Coverage"
              value={`${stats?.successRate || 0}%`}
              subtitle={`${stats?.repsSuccessful || 0} of ${stats?.repsAttempted || 0} reps successful | Attempt success: ${stats?.attemptSuccessRate || 0}%`}
              icon={CheckCircle}
              color="success"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <StatCard
              title="Total Errors"
              value={errors?.totalErrors || 0}
              subtitle={`${errors?.unresolvedErrors || 0} unresolved`}
              icon={Error}
              color="error"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <StatCard
              title="Timeout Syncs"
              value={stats?.timeoutSyncs || 0}
              subtitle="Timed out sync attempts"
              icon={Warning}
              color="warning"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <StatCard
              title="In Progress"
              value={stats?.inProgressSyncs || 0}
              subtitle="Active syncs"
              icon={Schedule}
              color="info"
            />
          </Grid>
        </Grid>

        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
          >
            <Tab label="Recent Activity" />
            <Tab label="Error Analysis" />
            <Tab label="Top Users" />
          </Tabs>
        </Box>

        {/* Recent Activity Tab */}
        {activeTab === 0 && (
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
              lg={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Recent Syncs
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ maxHeight: 360 }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Rep</TableCell>
                          <TableCell align="right">Attempts</TableCell>
                          <TableCell align="right">Completed</TableCell>
                          <TableCell align="right">Failed Attempts</TableCell>
                          <TableCell>Last Sync</TableCell>
                          <TableCell>Last Status</TableCell>
                          <TableCell>Successful Day</TableCell>
                          <TableCell>Last Error</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentActivity?.recentSyncs
                          ?.slice(
                            recentSyncPage * rowsPerPage,
                            recentSyncPage * rowsPerPage + rowsPerPage,
                          )
                          .map((sync) => {
                            const rowKey = getRecentSyncRowKey(sync);

                            return (
                              <TableRow key={rowKey}>
                                <TableCell>{sync.syncDay || "-"}</TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2">
                                      {sync.repName || sync.repId || "Unknown"}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {buildRepSyncMetaLine(sync)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  {sync.attempts || 0}
                                </TableCell>
                                <TableCell align="right">
                                  {sync.completedAttempts || 0}
                                </TableCell>
                                <TableCell align="right">
                                  {sync.errorAttempts || 0}
                                </TableCell>
                                <TableCell>
                                  {formatDateTime(sync.lastSyncAt)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={sync.lastSyncStatus || "-"}
                                    size="small"
                                    color={getStatusColor(sync.lastSyncStatus)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      sync.successfulThatDay ? "Yes" : "No"
                                    }
                                    size="small"
                                    color={
                                      sync.successfulThatDay
                                        ? "success"
                                        : "warning"
                                    }
                                  />
                                </TableCell>
                                <TableCell>{sync.lastError || "-"}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openSyncDetailModal(sync)}
                                  >
                                    Open Sync
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[rowsPerPage]}
                    count={recentActivity?.recentSyncs?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={recentSyncPage}
                    onPageChange={(e, p) => setRecentSyncPage(p)}
                    onRowsPerPageChange={() => {}}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Error Analysis Tab */}
        {activeTab === 1 && (
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Rep-Day Failure Queue
                  </Typography>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table
                      size="small"
                      stickyHeader
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Province</TableCell>
                          <TableCell>Supervisor</TableCell>
                          <TableCell>Rep</TableCell>
                          <TableCell align="right">Attempts</TableCell>
                          <TableCell align="right">Completed</TableCell>
                          <TableCell align="right">Failed Attempts</TableCell>
                          <TableCell>Last Sync</TableCell>
                          <TableCell>Last Status</TableCell>
                          <TableCell>Last Error</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {errors?.repDayFailureQueue
                          ?.slice(
                            failureQueuePage * rowsPerPage,
                            failureQueuePage * rowsPerPage + rowsPerPage,
                          )
                          .map((item) => (
                            <TableRow
                              key={`${String(item.repId || "unknown")}-${String(item.syncDay || "day")}`}
                            >
                              <TableCell>{item.syncDay || "-"}</TableCell>
                              <TableCell>{item.province || "-"}</TableCell>
                              <TableCell>
                                {item.supervisorName || "-"}
                              </TableCell>
                              <TableCell>
                                {item.repName || item.repId || "Unknown"}
                              </TableCell>
                              <TableCell align="right">
                                {item.attempts || 0}
                              </TableCell>
                              <TableCell align="right">
                                {item.completedAttempts || 0}
                              </TableCell>
                              <TableCell align="right">
                                {item.errorAttempts || 0}
                              </TableCell>
                              <TableCell>
                                {formatDateTime(item.lastSyncAt)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.lastSyncStatus || "-"}
                                  size="small"
                                  color={getStatusColor(item.lastSyncStatus)}
                                />
                              </TableCell>
                              <TableCell>{item.lastError || "-"}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    openAttemptsFromFailureQueue(item)
                                  }
                                >
                                  Open Attempts
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[rowsPerPage]}
                    count={errors?.repDayFailureQueue?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={failureQueuePage}
                    onPageChange={(e, p) => setFailureQueuePage(p)}
                    onRowsPerPageChange={() => {}}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Recovery Funnel
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      sm={4}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="warning.main"
                        >
                          {errors?.repDayRecoveryFunnel?.repDaysWithAnyError ||
                            0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Rep-days with any error
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="success.main"
                        >
                          {errors?.repDayRecoveryFunnel
                            ?.repDaysRecoveredSameDay || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Recovered same day
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="error.main"
                        >
                          {errors?.repDayRecoveryFunnel
                            ?.repDaysStillUnresolved || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Still unresolved
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Unresolved Aging Buckets
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={6}
                      sm={3}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="text.primary"
                        >
                          {errors?.unresolvedAgingBuckets?.["0to1h"] || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          0-1h
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={3}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="warning.main"
                        >
                          {errors?.unresolvedAgingBuckets?.["1to4h"] || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          1-4h
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={3}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="error.main"
                        >
                          {errors?.unresolvedAgingBuckets?.["4to24h"] || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          4-24h
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={3}
                    >
                      <Box
                        textAlign="center"
                        p={2}
                      >
                        <Typography
                          variant="h4"
                          color="error.main"
                        >
                          {errors?.unresolvedAgingBuckets?.over24h || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Over 24h
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Error Signatures
                  </Typography>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table
                      size="small"
                      stickyHeader
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Entity</TableCell>
                          <TableCell>Error Type</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Unresolved</TableCell>
                          <TableCell align="right">Affected Reps</TableCell>
                          <TableCell>First Seen</TableCell>
                          <TableCell>Last Seen</TableCell>
                          <TableCell>Sample Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {errors?.errorSignatures
                          ?.slice(
                            errorSignaturePage * rowsPerPage,
                            errorSignaturePage * rowsPerPage + rowsPerPage,
                          )
                          .map((item, index) => (
                            <TableRow
                              key={`${item.entity || "entity"}-${item.errorType || "type"}-${errorSignaturePage}-${index}`}
                            >
                              <TableCell>{item.entity || "-"}</TableCell>
                              <TableCell>{item.errorType || "-"}</TableCell>
                              <TableCell align="right">
                                {item.count || 0}
                              </TableCell>
                              <TableCell align="right">
                                {item.unresolvedCount > 0 ? (
                                  <Chip
                                    label={item.unresolvedCount}
                                    size="small"
                                    color="error"
                                  />
                                ) : (
                                  0
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {item.affectedRepCount || 0}
                              </TableCell>
                              <TableCell>
                                {formatDateTime(
                                  item.firstSeenAt,
                                  "MMM dd, HH:mm",
                                )}
                              </TableCell>
                              <TableCell>
                                {formatDateTime(
                                  item.lastSeenAt,
                                  "MMM dd, HH:mm",
                                )}
                              </TableCell>
                              <TableCell>{item.sampleMessage || "-"}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[rowsPerPage]}
                    count={errors?.errorSignatures?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={errorSignaturePage}
                    onPageChange={(e, p) => setErrorSignaturePage(p)}
                    onRowsPerPageChange={() => {}}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Top Users Tab */}
        {activeTab === 2 && (
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Most Active Users (Selected Date Range)
                  </Typography>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Province</TableCell>
                          <TableCell>Supervisor</TableCell>
                          <TableCell>Rep</TableCell>
                          <TableCell align="right">Total Syncs</TableCell>
                          <TableCell align="right">Errors</TableCell>
                          <TableCell>Last Sync</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users?.topUsers
                          ?.slice(
                            topUserPage * rowsPerPage,
                            topUserPage * rowsPerPage + rowsPerPage,
                          )
                          .map((user) => (
                            <TableRow key={user._id}>
                              <TableCell>{user.province || "-"}</TableCell>
                              <TableCell>
                                {user.supervisorName || "-"}
                              </TableCell>
                              <TableCell>
                                {user.repName || user.userName || user._id}
                              </TableCell>
                              <TableCell align="right">
                                {user.syncCount}
                              </TableCell>
                              <TableCell align="right">
                                {user.errorCount > 0 ? (
                                  <Chip
                                    label={user.errorCount}
                                    size="small"
                                    color="error"
                                  />
                                ) : (
                                  0
                                )}
                              </TableCell>
                              <TableCell>
                                {format(
                                  new Date(user.lastSync),
                                  "MMM dd, yyyy HH:mm",
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[rowsPerPage]}
                    count={users?.topUsers?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={topUserPage}
                    onPageChange={(e, p) => setTopUserPage(p)}
                    onRowsPerPageChange={() => {}}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Dialog
          open={Boolean(syncDetailModal.open)}
          onClose={closeSyncDetailModal}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Sync Details: {modalSync?.repName || modalSync?.repId || "Unknown"}{" "}
            ({modalSync?.syncDay || "-"})
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Chip
                  size="small"
                  label={`Attempts: ${modalSync?.attempts || 0}`}
                />
                <Chip
                  size="small"
                  label={`Completed: ${modalSync?.completedAttempts || 0}`}
                  color="success"
                />
                <Chip
                  size="small"
                  label={`Failed: ${modalSync?.errorAttempts || 0}`}
                  color="error"
                />
                <Chip
                  size="small"
                  label={modalSync?.lastSyncStatus || "-"}
                  color={getStatusColor(modalSync?.lastSyncStatus)}
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  Last Sync:{" "}
                  {formatDateTime(modalSync?.lastSyncAt, "MMM dd, HH:mm:ss")}
                </Typography>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Loaded Attempts: ${modalAttemptSummary.total}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  label={`Completed Attempts: ${modalAttemptSummary.completedAttempts}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  label={`Errored Attempts: ${modalAttemptSummary.errorAttempts}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={`Timeout Attempts: ${modalAttemptSummary.timeoutAttempts}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="info"
                  label={`In Progress Attempts: ${modalAttemptSummary.inProgressAttempts}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={modalRecordsLabel}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  label={`Error Records: ${modalAttemptSummary.errorRecords}`}
                />
              </Stack>
            </Box>

            {modalAttemptsLoading ? (
              <Box sx={{ py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            ) : modalAttemptsError ? (
              <Alert
                severity="error"
                sx={{ my: 1 }}
              >
                {modalAttemptsError}
              </Alert>
            ) : modalAttempts.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
              >
                No attempt details found.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Success</TableCell>
                      <TableCell align="right">Errors</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Last Error</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modalAttempts.flatMap((attempt) => {
                      const attemptSyncId = getAttemptSyncId(attempt);
                      const attemptRowKey = `${modalRowKey}-${attemptSyncId || "attempt"}`;
                      const telemetryExpanded =
                        expandedAttemptTelemetryByRow[modalRowKey] ===
                        attemptSyncId;
                      const telemetryLoading =
                        attemptTelemetryLoadingBySyncId[attemptSyncId];
                      const telemetryError =
                        attemptTelemetryErrorBySyncId[attemptSyncId] || "";
                      const telemetry =
                        attemptTelemetryBySyncId[attemptSyncId] || null;
                      const entityRows = normalizeEntityRowsForDisplay(
                        Array.isArray(telemetry?.byEntity)
                          ? telemetry.byEntity
                          : [],
                      );
                      const entitySummary = summarizeEntityRows(entityRows);

                      return [
                        <TableRow key={attemptRowKey}>
                          <TableCell>
                            {formatDateTime(
                              attempt.startedAt,
                              "MMM dd, HH:mm:ss",
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(
                              attempt.endedAt,
                              "MMM dd, HH:mm:ss",
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatDuration(attempt.durationMs)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={attempt.status || "-"}
                              size="small"
                              color={getStatusColor(attempt.status)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {attempt.successCount || 0}
                          </TableCell>
                          <TableCell align="right">
                            {attempt.errorCount || 0}
                          </TableCell>
                          <TableCell align="right">
                            {attempt.totalRecords || 0}
                          </TableCell>
                          <TableCell>{attempt.lastError || "-"}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!attemptSyncId}
                              onClick={() =>
                                toggleAttemptTelemetry(modalRowKey, attempt)
                              }
                            >
                              {telemetryExpanded
                                ? "Hide Details"
                                : "View Details"}
                            </Button>
                          </TableCell>
                        </TableRow>,
                        <TableRow key={`${attemptRowKey}-entity-detail`}>
                          <TableCell
                            colSpan={9}
                            sx={{
                              py: 0,
                              borderBottom: telemetryExpanded ? undefined : 0,
                            }}
                          >
                            <Collapse
                              in={telemetryExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ m: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ mb: 1 }}
                                >
                                  By Entity
                                </Typography>
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  useFlexGap
                                  flexWrap="wrap"
                                  sx={{ mb: 1 }}
                                >
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Entities: ${entitySummary.total}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    label={`Completed: ${entitySummary.completed}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    label={`Errors: ${entitySummary.error}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={`Timeouts: ${entitySummary.timeout}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    label={`In Progress: ${entitySummary.inProgress}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`With Events: ${entitySummary.entitiesWithEvents}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Total Events: ${entitySummary.totalEvents}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Entities with Counts: ${entitySummary.entitiesWithKnownCounts}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Down: ${entitySummary.downloadedRecords}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Up: ${entitySummary.uploadedRecords}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Entity Records: ${entitySummary.successRecords}/${Math.max(entitySummary.totalRecords, entitySummary.successRecords + entitySummary.errorRecords)} success`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    label={`Entity Error Records: ${entitySummary.errorRecords}`}
                                  />
                                </Stack>
                                {telemetryLoading ? (
                                  <Box sx={{ py: 1 }}>
                                    <CircularProgress size={20} />
                                  </Box>
                                ) : telemetryError ? (
                                  <Alert
                                    severity="error"
                                    sx={{ my: 1 }}
                                  >
                                    {telemetryError}
                                  </Alert>
                                ) : entityRows.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    No entity details available for this
                                    attempt.
                                  </Typography>
                                ) : (
                                  <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                  >
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell width={40}></TableCell>
                                          <TableCell>Entity</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Started</TableCell>
                                          <TableCell>Finished</TableCell>
                                          <TableCell>Last Update</TableCell>
                                          <TableCell align="right">
                                            Events
                                          </TableCell>
                                          <TableCell align="right">
                                            Down
                                          </TableCell>
                                          <TableCell align="right">
                                            Up
                                          </TableCell>
                                          <TableCell align="right">
                                            Success
                                          </TableCell>
                                          <TableCell align="right">
                                            Errors
                                          </TableCell>
                                          <TableCell align="right">
                                            Records
                                          </TableCell>
                                          <TableCell>Last Message</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {entityRows.flatMap((entityRow) => {
                                          const entityKey = String(
                                            entityRow?.entity || "entity",
                                          );
                                          const entityExpanded =
                                            expandedEntityBySyncId[
                                              attemptSyncId
                                            ] === entityKey;
                                          const events = Array.isArray(
                                            entityRow?.events,
                                          )
                                            ? entityRow.events
                                            : [];
                                          const entityCounts =
                                            deriveEntityCounts(entityRow);
                                          const entityDown = Number.isFinite(
                                            entityCounts.downloaded,
                                          )
                                            ? entityCounts.downloaded
                                            : null;
                                          const entityUp = Number.isFinite(
                                            entityCounts.uploaded,
                                          )
                                            ? entityCounts.uploaded
                                            : null;
                                          const entityKnownTotal =
                                            Number.isFinite(entityCounts.total)
                                              ? entityCounts.total
                                              : Number.isFinite(
                                                    entityCounts.success,
                                                  ) ||
                                                  Number.isFinite(
                                                    entityCounts.error,
                                                  )
                                                ? (Number.isFinite(
                                                    entityCounts.success,
                                                  )
                                                    ? entityCounts.success
                                                    : 0) +
                                                  (Number.isFinite(
                                                    entityCounts.error,
                                                  )
                                                    ? entityCounts.error
                                                    : 0)
                                                : null;

                                          return [
                                            <TableRow
                                              key={`${attemptRowKey}-${entityKey}`}
                                            >
                                              <TableCell>
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    toggleEntityDetail(
                                                      attemptSyncId,
                                                      entityKey,
                                                    )
                                                  }
                                                >
                                                  {entityExpanded ? (
                                                    <KeyboardArrowUp fontSize="small" />
                                                  ) : (
                                                    <KeyboardArrowDown fontSize="small" />
                                                  )}
                                                </IconButton>
                                              </TableCell>
                                              <TableCell>{entityKey}</TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={
                                                    entityRow?.currentStatus ||
                                                    "-"
                                                  }
                                                  size="small"
                                                  color={getStatusColor(
                                                    entityRow?.currentStatus,
                                                  )}
                                                />
                                              </TableCell>
                                              <TableCell>
                                                {formatDateTime(
                                                  entityRow?.startedAt,
                                                  "MMM dd, HH:mm:ss",
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {formatDateTime(
                                                  entityRow?.finishedAt,
                                                  "MMM dd, HH:mm:ss",
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {formatDateTime(
                                                  entityRow?.lastUpdateAt,
                                                  "MMM dd, HH:mm:ss",
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {Number(
                                                  entityRow?.eventCount ||
                                                    events.length ||
                                                    0,
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {Number.isFinite(entityDown)
                                                  ? entityDown
                                                  : "-"}
                                              </TableCell>
                                              <TableCell align="right">
                                                {Number.isFinite(entityUp)
                                                  ? entityUp
                                                  : "-"}
                                              </TableCell>
                                              <TableCell align="right">
                                                {Number.isFinite(
                                                  entityCounts.success,
                                                )
                                                  ? entityCounts.success
                                                  : "-"}
                                              </TableCell>
                                              <TableCell align="right">
                                                {Number.isFinite(
                                                  entityCounts.error,
                                                )
                                                  ? entityCounts.error
                                                  : "-"}
                                              </TableCell>
                                              <TableCell align="right">
                                                {Number.isFinite(
                                                  entityKnownTotal,
                                                )
                                                  ? entityKnownTotal
                                                  : "-"}
                                              </TableCell>
                                              <TableCell>
                                                {entityRow?.lastMessage || "-"}
                                              </TableCell>
                                            </TableRow>,
                                            <TableRow
                                              key={`${attemptRowKey}-${entityKey}-detail`}
                                            >
                                              <TableCell
                                                colSpan={13}
                                                sx={{
                                                  py: 0,
                                                  borderBottom: entityExpanded
                                                    ? undefined
                                                    : 0,
                                                }}
                                              >
                                                <Collapse
                                                  in={entityExpanded}
                                                  timeout="auto"
                                                  unmountOnExit
                                                >
                                                  <Box sx={{ p: 1 }}>
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      Last Details
                                                    </Typography>
                                                    <Box
                                                      component="pre"
                                                      sx={{
                                                        backgroundColor:
                                                          "grey.100",
                                                        p: 1,
                                                        mt: 0.5,
                                                        mb: 1,
                                                        borderRadius: 1,
                                                        overflow: "auto",
                                                        whiteSpace: "pre-wrap",
                                                        fontSize: 12,
                                                      }}
                                                    >
                                                      {formatObjectDetails(
                                                        entityRow?.lastDetails,
                                                      )}
                                                    </Box>
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      Events
                                                    </Typography>
                                                    {events.length === 0 ? (
                                                      <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{ mt: 0.5 }}
                                                      >
                                                        No events.
                                                      </Typography>
                                                    ) : (
                                                      <TableContainer
                                                        component={Paper}
                                                        variant="outlined"
                                                        sx={{ mt: 0.5 }}
                                                      >
                                                        <Table size="small">
                                                          <TableHead>
                                                            <TableRow>
                                                              <TableCell>
                                                                At
                                                              </TableCell>
                                                              <TableCell>
                                                                Status
                                                              </TableCell>
                                                              <TableCell>
                                                                Message
                                                              </TableCell>
                                                            </TableRow>
                                                          </TableHead>
                                                          <TableBody>
                                                            {events.map(
                                                              (
                                                                event,
                                                                eventIndex,
                                                              ) => (
                                                                <TableRow
                                                                  key={`${attemptRowKey}-${entityKey}-event-${eventIndex}`}
                                                                >
                                                                  <TableCell>
                                                                    {formatDateTime(
                                                                      event?.at,
                                                                      "MMM dd, HH:mm:ss",
                                                                    )}
                                                                  </TableCell>
                                                                  <TableCell>
                                                                    {event?.status ||
                                                                      "-"}
                                                                  </TableCell>
                                                                  <TableCell>
                                                                    {event?.message ||
                                                                      "-"}
                                                                  </TableCell>
                                                                </TableRow>
                                                              ),
                                                            )}
                                                          </TableBody>
                                                        </Table>
                                                      </TableContainer>
                                                    )}
                                                  </Box>
                                                </Collapse>
                                              </TableCell>
                                            </TableRow>,
                                          ];
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>,
                      ];
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={refreshSyncDetailModal}
              disabled={!modalSync || !modalRowKey || modalAttemptsLoading}
            >
              Refresh
            </Button>
            <Button onClick={closeSyncDetailModal}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
};

export default SyncDashboard;
